<template>
  <div class="team-quotas">
    <div class="team-quotas__head">
      <label class="form-create__label">{{ $t("quotas.label") }}</label>
      <p class="team-quotas__hint">
        {{ $t("quotas.hintMode") }}
      </p>
    </div>

    <div v-if="loading" class="team-quotas__hint">{{ $t("common.loading") }}</div>

    <template v-else>
      <div class="team-quotas__modes">
        <label>
          <input type="radio" value="all" v-model="form.team_mode" />
          {{ $t("quotas.modeAll") }}
        </label>
        <label>
          <input type="radio" value="selected" v-model="form.team_mode" />
          {{ $t("quotas.modeSelected") }}
        </label>
      </div>

      <div class="team-quotas__switches">
        <label>
          <input type="checkbox" v-model="form.allow_leads_without_team" />
          {{ $t("quotas.allowWithoutTeam") }}
        </label>
        <label>
          <input type="checkbox" v-model="form.split_buyer_quota" />
          {{ $t("quotas.splitBuyerQuota") }}
        </label>
        <label class="team-quotas__hour">
          {{ $t("quotas.releaseHour") }}
          <input
            type="number"
            min="0"
            max="23"
            :placeholder="$t('quotas.releaseHourPlaceholder')"
            :value="form.release_hour"
            @input="setReleaseHour($event.target.value)"
          />
        </label>
      </div>

      <p class="team-quotas__hint">
        {{ $t("quotas.hintRelease") }}
      </p>

      <table class="team-quotas__table">
        <thead>
          <tr>
            <th>{{ $t("quotas.colAllow") }}</th>
            <th>{{ $t("quotas.colTeam") }}</th>
            <th>{{ $t("quotas.colShare") }}</th>
            <th>{{ $t("quotas.colQuota") }}</th>
            <th>{{ $t("quotas.colAcceptedToday") }}</th>
            <th>{{ $t("quotas.colInProgress") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="team in teams">
            <tr :key="`team-${team.id}`">
              <td>
                <input type="checkbox" v-model="rules[team.id].is_allowed" />
              </td>
              <td>{{ team.name }}</td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  v-model.number="rules[team.id].percent"
                  :disabled="!rules[team.id].is_allowed"
                />
              </td>
              <td>{{ quotaOf(rules[team.id].percent) }}</td>
              <td>{{ usageOf(team.id).accepted }}</td>
              <td>{{ usageOf(team.id).in_flight }}</td>
              <td>
                <button type="button" class="team-quotas__link" @click="toggleTeam(team.id)">
                  {{ opened[team.id] ? $t("quotas.collapse") : $t("quotas.buyers") }}
                </button>
              </td>
            </tr>

            <tr v-if="opened[team.id]" :key="`buyers-${team.id}`" class="team-quotas__buyers-row">
              <td colspan="7">
                <div v-if="!buyers[team.id]" class="team-quotas__hint">
                  {{ $t("quotas.loadingBuyers") }}
                </div>
                <div v-else-if="!buyers[team.id].length" class="team-quotas__hint">
                  {{ $t("quotas.noBuyers") }}
                </div>
                <table v-else class="team-quotas__table team-quotas__table--inner">
                  <tbody>
                    <tr v-for="buyer in buyers[team.id]" :key="`buyer-${buyer.id}`">
                      <td>
                        <input type="checkbox" v-model="buyerRules[buyer.id].is_allowed" />
                      </td>
                      <td>{{ buyer.name }}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          v-model.number="buyerRules[buyer.id].percent"
                          :disabled="!form.split_buyer_quota || !buyerRules[buyer.id].is_allowed"
                        />
                      </td>
                      <td>
                        {{
                          form.split_buyer_quota
                            ? quotaOf((rules[team.id].percent * buyerRules[buyer.id].percent) / 100)
                            : "—"
                        }}
                      </td>
                      <td>{{ buyerUsageOf(buyer.id).accepted }}</td>
                      <td>{{ buyerUsageOf(buyer.id).in_flight }}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
                <p class="team-quotas__hint">
                  {{ $t("quotas.hintBuyers") }}
                </p>
              </td>
            </tr>
          </template>

          <tr v-if="usage" class="team-quotas__total">
            <td></td>
            <td>{{ $t("quotas.withoutTeam") }}</td>
            <td>—</td>
            <td>
              {{ form.allow_leads_without_team ? $t("quotas.allowed") : $t("quotas.notAllowed") }}
            </td>
            <td>{{ noTeamUsage.accepted }}</td>
            <td>{{ noTeamUsage.in_flight }}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div class="team-quotas__summary">
        Send Capacity: <b>{{ capacity }}</b> · {{ $t("quotas.summary") }}
        <b :class="percentSum > 100 && 'team-quotas__warn'">{{ percentSum }}%</b>
      </div>

      <ul v-if="warnings.length" class="team-quotas__warnings">
        <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
      </ul>

      <div class="team-quotas__actions">
        <button type="button" class="team-quotas__save" :disabled="saving" @click="save">
          {{ saving ? $t("common.saving") : $t("quotas.saveQuotas") }}
        </button>
        <span v-if="saved" class="team-quotas__saved">{{ $t("common.saved") }}</span>
        <span v-if="error" class="team-quotas__warn">{{ error }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import leadApi from "~/api/lead";
import userApi from "~/api/user";

// Квоты команд и байеров внутри интеграции: допуск и доли от Send Capacity.
// Сохраняется отдельным запросом, чтобы правка долей не перетирала ключи партнёра.
export default {
  name: "TeamQuotasField",
  props: {
    integrationId: { type: [Number, String], required: true },
    // Send Capacity из формы интеграции: по ней считаем, сколько лидов выходит из процента
    sendCapacity: { type: [Number, String], default: 0 },
  },
  data() {
    return {
      loading: true,
      saving: false,
      saved: false,
      error: "",
      form: {
        team_mode: "all",
        split_buyer_quota: false,
        release_hour: null,
        allow_leads_without_team: false,
      },
      teams: [],
      rules: {},
      buyers: {},
      buyerRules: {},
      opened: {},
      usage: null,
      savedMode: "all",
    };
  },
  computed: {
    capacity() {
      return Number(this.sendCapacity) || 0;
    },
    percentSum() {
      return this.teams
        .filter((team) => this.rules[team.id] && this.rules[team.id].is_allowed)
        .reduce((sum, team) => sum + (Number(this.rules[team.id].percent) || 0), 0);
    },
    noTeamUsage() {
      const row = (this.usage && this.usage.teams.find((item) => !item.team_id)) || {};
      return { accepted: row.accepted || 0, in_flight: row.in_flight || 0 };
    },
    // «что изменится после сохранения» — показываем до нажатия, а не после
    warnings() {
      const list = [];

      if (this.form.team_mode === "selected" && this.savedMode === "all") {
        const blocked = this.teams.filter((team) => !this.rules[team.id].is_allowed);
        list.push(
          blocked.length
            ? this.$t("quotas.warnBlocked", {
                teams: blocked.map((team) => team.name).join(", "),
              })
            : this.$t("quotas.warnAllSelected"),
        );
      }

      if (this.form.team_mode === "selected" && !this.form.allow_leads_without_team) {
        list.push(this.$t("quotas.warnWithoutTeam"));
      }

      if (this.form.team_mode === "selected" && this.percentSum > 100) {
        list.push(this.$t("quotas.warnOverCap", { percent: this.percentSum }));
      }

      if (this.form.team_mode === "selected" && this.percentSum < 100 && this.percentSum > 0) {
        list.push(this.$t("quotas.warnUnderCap", { percent: this.percentSum }));
      }

      if (this.form.split_buyer_quota && this.form.team_mode !== "selected") {
        list.push(this.$t("quotas.warnSplitNoop"));
      }

      return list;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    setReleaseHour(value) {
      const clean = String(value).trim();
      this.form.release_hour = clean === "" ? null : Math.max(0, Math.min(23, Number(clean)));
    },
    quotaOf(percent) {
      return Math.floor((this.capacity * (Number(percent) || 0)) / 100);
    },
    usageOf(teamId) {
      const row = (this.usage && this.usage.teams.find((item) => item.team_id === teamId)) || {};
      return { accepted: row.accepted || 0, in_flight: row.in_flight || 0 };
    },
    buyerUsageOf(userId) {
      const row = (this.usage && this.usage.buyers.find((item) => item.user_id === userId)) || {};
      return { accepted: row.accepted || 0, in_flight: row.in_flight || 0 };
    },
    load() {
      this.loading = true;

      Promise.all([
        userApi.getTeamAll(),
        leadApi.getIntegrationQuotas(this.integrationId),
        leadApi.getIntegrationQuotaUsage(this.integrationId).catch(() => null),
      ])
        .then(([teamsRes, quotasRes, usageRes]) => {
          this.teams = (teamsRes && teamsRes.data && teamsRes.data.items) || [];
          this.usage = (usageRes && usageRes.data) || null;

          const quotas = (quotasRes && quotasRes.data) || {};

          this.form = {
            team_mode: quotas.team_mode || "all",
            split_buyer_quota: !!quotas.split_buyer_quota,
            release_hour: quotas.release_hour === undefined ? null : quotas.release_hour,
            allow_leads_without_team: !!quotas.allow_leads_without_team,
          };
          this.savedMode = this.form.team_mode;

          const saved = {};
          (quotas.team_rules || []).forEach((rule) => {
            saved[rule.team_id] = rule;
          });

          const rules = {};
          this.teams.forEach((team) => {
            const rule = saved[team.id];
            rules[team.id] = {
              is_allowed: rule ? !!rule.is_allowed : true,
              percent: rule ? Number(rule.percent) : 0,
            };
          });
          this.rules = rules;

          const buyerRules = {};
          (quotas.buyer_rules || []).forEach((rule) => {
            buyerRules[rule.user_id] = {
              team_id: rule.team_id,
              is_allowed: !!rule.is_allowed,
              percent: Number(rule.percent),
            };
          });
          this.buyerRules = buyerRules;
        })
        .catch((e) => {
          this.error = this.messageOf(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggleTeam(teamId) {
      Vue.set(this.opened, teamId, !this.opened[teamId]);

      if (this.opened[teamId] && !this.buyers[teamId]) {
        this.loadBuyers(teamId);
      }
    },
    loadBuyers(teamId) {
      userApi
        .getUserTeamList(`${teamId}?perPage=200&page=1`)
        .then((res) => {
          // этот эндпоинт отдаёт пагинатор; у фильтра тот же список лежит в items.data
          const items =
            (res && res.data && (res.data.data || (res.data.items && res.data.items.data))) || [];
          Vue.set(this.buyers, teamId, items);

          items.forEach((buyer) => {
            if (!this.buyerRules[buyer.id]) {
              // байера без сохранённого правила не ограничиваем
              Vue.set(this.buyerRules, buyer.id, {
                team_id: teamId,
                is_allowed: false,
                percent: 0,
              });
            } else {
              this.buyerRules[buyer.id].team_id = teamId;
            }
          });
        })
        .catch(() => {
          Vue.set(this.buyers, teamId, []);
        });
    },
    save() {
      // Блок сохраняется и своей кнопкой, и общим Submit формы интеграции.
      // Пока справочники не загрузились, team_rules собрать не из чего, а пустой
      // список на сервере означает «удалить все правила» — поэтому молча выходим.
      if (this.loading || !this.teams.length) {
        return;
      }

      this.saving = true;
      this.saved = false;
      this.error = "";

      const payload = {
        team_mode: this.form.team_mode,
        split_buyer_quota: this.form.split_buyer_quota,
        release_hour: this.form.release_hour,
        allow_leads_without_team: this.form.allow_leads_without_team,
        team_rules: this.teams.map((team) => ({
          team_id: team.id,
          is_allowed: !!this.rules[team.id].is_allowed,
          percent: Number(this.rules[team.id].percent) || 0,
        })),
        // отправляем только отмеченных байеров: пустой список внутри команды = проходят все
        buyer_rules: Object.keys(this.buyerRules)
          .filter((userId) => this.buyerRules[userId].is_allowed)
          .map((userId) => ({
            user_id: Number(userId),
            team_id: this.buyerRules[userId].team_id || null,
            is_allowed: true,
            percent: Number(this.buyerRules[userId].percent) || 0,
          })),
      };

      leadApi
        .updateIntegrationQuotas(this.integrationId, payload)
        .then(() => {
          this.saved = true;
          this.savedMode = this.form.team_mode;
        })
        .catch((e) => {
          this.error = this.messageOf(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    messageOf(e) {
      return (
        (e && e.response && e.response.data && e.response.data.message) || this.$t("common.failed")
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.team-quotas {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__hint {
    font-size: 13px;
    color: $mainSecondTextColor;
  }

  &__modes,
  &__switches {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    font-size: 14px;

    label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
  }

  &__hour input {
    width: 70px;
    height: 30px;
    padding: 0 6px;
    border: 1px solid $mainDisabled;
    border-radius: 7px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th,
    td {
      padding: 6px 8px;
      border-bottom: 1px solid $mainDisabled;
      text-align: left;
    }

    th {
      font-size: 12px;
      font-weight: 600;
      color: $mainSecondTextColor;
    }

    input[type="number"] {
      width: 70px;
      height: 28px;
      padding: 0 6px;
      border: 1px solid $mainDisabled;
      border-radius: 7px;
    }

    &--inner {
      margin: 4px 0;
    }
  }

  &__buyers-row > td {
    background: rgba(0, 0, 0, 0.02);
  }

  &__link {
    border: 0;
    background: none;
    color: $coffee;
    cursor: pointer;
    text-decoration: underline;
  }

  &__summary {
    font-size: 14px;
  }

  &__warn {
    color: #c0392b;
  }

  &__warnings {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    color: #a9622a;

    li {
      margin-bottom: 2px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__save {
    height: 34px;
    padding: 0 18px;
    border: 0;
    border-radius: 7px;
    background: $coffee;
    color: #fff;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__saved {
    font-size: 13px;
    color: #2d8a4e;
  }
}
</style>
